import React, { memo, useCallback, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import FilterList from '@mui/icons-material/FilterList';
import { Badge, Box, Button, CircularProgress, Divider, MenuItem, Stack, Typography, Grid } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import { useTheme } from '@mui/material/styles';
import {
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarExportContainer,
	gridFilterModelSelector,
	useGridApiContext,
	useGridSelector
} from '@mui/x-data-grid-premium';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import strings from '../../Globalization/index';
import util from '../../util';
import consts from '../../consts';
import { useGridBase } from './GridBaseContext';
import GridPreferencesControl from './GridPreferencesControl';
import HeaderFilterModelForm from './HeaderFilterModelForm';
// Wrapper component to prevent 'hideMenu' from being passed to MenuItem
const MenuItemWrapper = React.forwardRef((props, ref) => {
  const { hideMenu, ...restProps } = props;
  return <MenuItem {...restProps} ref={ref} />;
});

export const { viewOnly } = util;

const GridBaseToolbar = (props) => {
	const {
		handleImportfile,
		handleOnClickAdd,
		modelConfig,
		disableExport: disableExportProp,
		standaloneFilterColumns,
		readOnly: readOnlyProp,
		onExport,
		combos,
		onExportToURSX,
		standaloneFilters,
		onSubmitHeaderFilters,
		isRelation,
		clearSelection,
		handelXML,
		activeRecord,
		relationTitle,
		refresh,
		defaultSort,
		onChangePreference,
		columnsVisibilityModel,
		gridId,
		onRefresh,
		defaultColumnVisibilityModel,
		printBarcode,
		defaultColumnWidths
	} = props
	const location = useLocation();
	const navigate = useNavigate();
	const theme = useTheme();
	const { onChangeHeaderFiltersVisibility } = useGridBase();
	const [exporting, setExporting] = useState(false);
	const apiRef = useGridApiContext();
	const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
	const selectedRowId = useGridSelector(apiRef, ({ rowSelectionModel }) =>
		rowSelectionModel?.ids?.size !== 0 ? rowSelectionModel : new Set()
	);
	const { isLoading, getState, rowSelectionModel, onRowSelectionModel } = useGridBase();
	const gridData = useMemo(() => {
		const gridBaseState = getState();
		return {
			rowCount: 0,
			rows: [],
			...gridBaseState?.data
		};
	}, [isLoading]);
	const auth = useSelector(state => state.auth);
	const {
		readOnly,
		disableAdd,
		disableDelete,
		disableExport,
		disableFiltering,
		disableColumnSelector,
		allowImport,
		allowURSX,
		disableExportCSV,
		disablePrintBarcode,
		disableSelectAll
	} = useMemo(() => {
		const readOnly =
			readOnlyProp ||
			(typeof modelConfig.readOnly === 'function'
				? modelConfig.readOnly({ gridData, location })
				: modelConfig.readOnly);
		const disableAdd =
			typeof modelConfig.disableAdd === 'function'
				? modelConfig.disableAdd({ gridData, location })
				: modelConfig.disableAdd;
		const disableDelete =
			typeof modelConfig.disableDelete === 'function'
				? modelConfig.disableDelete({ gridData, location, activeRecord })
				: modelConfig.disableDelete;
		const disableFiltering =
			typeof modelConfig.disableFiltering === 'function'
				? modelConfig.disableFiltering({ gridData, location })
				: modelConfig.disableFiltering;
		const disableColumnSelector =
			typeof modelConfig.disableColumnSelector === 'function'
				? modelConfig.disableColumnSelector({ gridData, location })
				: modelConfig.disableColumnSelector;
		const allowImport =
			typeof modelConfig.allowImport === 'function'
				? modelConfig.allowImport({ gridData, location })
				: modelConfig.allowImport;
		const disableExport = disableExportProp
			? disableExportProp
			: typeof modelConfig.disableExport === 'function'
				? modelConfig.disableExport({ gridData, location, auth })
				: modelConfig.disableExport;
		const allowURSX =
			typeof modelConfig.allowURSX === 'function'
				? modelConfig.allowURSX({ gridData, location })
				: modelConfig.allowURSX;
		const disableExportCSV =
			typeof modelConfig.disableExportCSV === 'function'
				? modelConfig.disableExportCSV({ gridData, location })
				: modelConfig.disableExportCSV;
		const disablePrintBarcode =
			typeof modelConfig.disablePrintBarcode === 'function'
				? modelConfig.disablePrintBarcode({ gridData, location })
				: modelConfig?.disablePrintBarcode !== undefined
					? modelConfig?.disablePrintBarcode
					: true;
		const disableSelectAll =
			typeof modelConfig.disableSelectAll === 'function'
				? modelConfig.disableSelectAll({ gridData, location })
				: modelConfig?.disableSelectAll !== undefined
					? modelConfig?.disableSelectAll
					: true;

		return {
			readOnly,
			disableAdd,
			disableDelete,
			disableFiltering,
			disableExport,
			disableColumnSelector,
			allowImport,
			allowURSX,
			disableExportCSV,
			disablePrintBarcode,
			disableSelectAll
		};
	}, [location, isLoading]);

	const handleOnExportToURSX = (name, extension) => (event) => {
		if (typeof onExportToURSX === 'function') {
			onExportToURSX({ event, name, extension });
		}
	};

	const handleOnExport = useCallback(
		async (type) => {
			setExporting(true);
			try {
				if (typeof onExport === 'function') {
					await onExport(type);
				}
			} catch (error) { /* empty */ }
			setExporting(false);
		},
		[onExport]
	);

	const handlePrintBarcode = async (event) => {
		if (typeof printBarcode === 'function') {
			const selectedRow = Array.from(apiRef?.current?.getSelectedRows()?.values());
			const ids = selectedRow?.map((row) => row?._id || row?.id);
			await printBarcode({ event, ids, selectedRow });
		}
	};

	const handleSelect = async (event) => {
		const rowsIds = apiRef?.current?.getAllRowIds?.();
		if ((rowsIds?.length > 0 && selectedRowId?.length === 0) || rowSelectionModel.ids.size === 0 ) {
			apiRef?.current?.setRowSelectionModel?.(rowsIds);
			onRowSelectionModel({
				...rowSelectionModel,
				ids: new Set(rowsIds)
			});
		} else {
			apiRef?.current?.setRowSelectionModel?.([]);
			onRowSelectionModel({
				...rowSelectionModel,
				ids: new Set()
			});
		}
	};

	const showExportButton = () => {
		if (auth?.tags?.ProductType.includes('catviewer')) {
			return disableExport;
		}
		if (consts.endoManagementRoute.includes(location.pathname) || consts.catDataAdminRoute.includes(location.pathname)) {
			return false;
		}
		return true;
	}

	return (
		<Stack>
			<Grid
				container
				spacing={0.75}
				sx={{
					'& .NewBtn': {
						backgroundColor: `${theme.palette.success.main} !important`,
						'&:hover': {
							backgroundColor: `${theme.palette.success.dark}`
						}
					},
					'& .column': {
						backgroundColor: '#1C84C6',
						'&:hover': {
							backgroundColor: '#1C84C6'
						}
					},
					'& .filter': {
						backgroundColor: '#A49F9D',
						'&:hover': {
							backgroundColor: '#A49F9D'
						}
					},
					'& .export': {
						backgroundColor: '#F8996B',
						'&:hover': {
							backgroundColor: '#F8996B'
						}
					}
				}}
			>
				{isRelation && (
					<Grid size={12} sx={{ display: 'flex', alignItems: 'center' }}>
						{(relationTitle || modelConfig.title) && (
							<Typography component="h5" variant="h5" color="primary" gutterBottom>
								{`${strings[modelConfig.title] || modelConfig.title || ''} ${relationTitle || ''}`}
							</Typography>
						)}
						<Box sx={{ flex: 1 }} />
						<Button
							onClick={clearSelection}
							title={strings.Close}
							color="error"
							variant="contained"
							size="small"
							sx={{
								px: 1,
								fontSize: 14,
								textTransform: 'uppercase',
								fontWeight: 'bold',
							}}
						>
							{strings.Close}
						</Button>
					</Grid>
				)}
				<Grid size={12}>
					<GridToolbarContainer sx={{ marginTop: 1, justifyContent: 'flex-start' }}>
						{!disableAdd && (
							<Button
								variant="contained"
								className="NewBtn"
								onClick={handleOnClickAdd}
								startIcon={<AddIcon />}
							>
								{strings.New}
							</Button>
						)}
						{readOnly === true || (disableAdd && disableDelete) ? null : (
							<Divider orientation="vertical" flexItem />
						)}
						{!disableColumnSelector && <GridToolbarColumnsButton variant="contained" className="column" slotProps={{ button: { variant: 'contained' } }}/>}
						{!disableFiltering && (
							<Badge color="primary" badgeContent={filterModel?.items?.length} showZero={false}>
								<Button
									startIcon={<FilterList />}
									onClick={onChangeHeaderFiltersVisibility}
									size="small"
									color='primary'
									variant="contained"
									className="filter"
								>
									{strings.Filters || 'Filters'}
								</Button>
							</Badge>
						)}
						{/*  it's for future use {!disableFiltering && <GridToolbarFilterButton variant="contained" className="filter" />} */}
						{allowURSX && (
							<GridToolbarExportContainer slotProps={{ button: { variant: 'contained', color: 'success' } }} apiRef={apiRef} variant="contained" className="export">
								<MenuItemWrapper onClick={handleOnExportToURSX('USERS', '.USRX')}>{strings.User}</MenuItemWrapper>
								<MenuItemWrapper onClick={handleOnExportToURSX('XUSERS', '.XSRX')}>
									{strings.PhysiciansAssistants}
								</MenuItemWrapper>
							</GridToolbarExportContainer>
						)}
						{!showExportButton() && (
							<GridToolbarExportContainer
								variant="contained"
								className="export"
								slotProps={{ button: { variant: 'contained', color: 'warning' } }}
							>
								<MenuItemWrapper onClick={() => handleOnExport('XLSX')}>{strings.Excel}</MenuItemWrapper>
								<MenuItemWrapper onClick={() => handleOnExport('PDF')}>{strings.PDF}</MenuItemWrapper>
								{!disableExportCSV && (
									<MenuItemWrapper onClick={() => handleOnExport('CSV')}>{strings.CSV}</MenuItemWrapper>
								)}
							</GridToolbarExportContainer>
						)}

						{exporting && (
							<Box spacing={1} display={'flex'} alignItems={'center'} justifyContent={'center'}>
								<CircularProgress size={18} />
							</Box>
						)}

						{!!gridId && (
							<GridPreferencesControl
								modelConfig={modelConfig}
								defaultSort={defaultSort}
								standaloneFilters={standaloneFilters}
								onChange={onChangePreference}
								defaultColumnVisibilityModel={defaultColumnVisibilityModel}
								gridId={gridId}
								defaultColumnWidths={defaultColumnWidths}
							/>
						)}
						{allowImport ? (
							<>
								<label htmlFor={`upload-photo-${modelConfig.name}`}>
									<input
										style={{ display: 'none' }}
										id={`upload-photo-${modelConfig.name}`}
										name={`upload-${modelConfig.name}`}
										type="file"
										accept={modelConfig?.importProps?.fileType}	
										onChange={(e) => {
											handleImportfile(e);
										}}
									/>

									<Button
										variant="contained"
										component="span"
										style={{ padding: '0px 18px', height: '30px' }}
										startIcon={<ImportExportIcon />}
									>
										{strings.Import}
									</Button>
								</label>
							</>
						) : null}
						{Array.isArray(modelConfig.gridToolbarCustomActions) &&
							modelConfig.gridToolbarCustomActions.map(
								({ Component: ActionComponent = Button, text, children, ...actionProps }, i) => (
									<ActionComponent
										navigate={navigate}
										location={location}
										refresh={refresh}
										combos={combos}
										clearSelection={clearSelection}
										data={gridData}
										onExport={onExport}
										onRefresh={onRefresh}
										{...actionProps}
										key={`gridToolbarCustomAction-${i}`}
									>
										{text || children}
									</ActionComponent>
								)
							)}

						{!disablePrintBarcode && (
							<Button startIcon={<LocalPrintshopIcon />} variant="contained" onClick={handlePrintBarcode}>
								{strings.PrintBarcode}
							</Button>
						)}
						{!disableSelectAll && (
							<Button
								disabled={viewOnly()}
								startIcon={rowSelectionModel.ids.size !== 0 ? <RemoveDoneIcon /> : <DoneAllIcon />}
								variant="contained"
								onClick={handleSelect}
							>
								{rowSelectionModel.ids.size !== 0 ? strings.UnSelectAll : strings.SelectAll}
							</Button>
						)}
					</GridToolbarContainer>
				</Grid>
				<Grid size={12}>
					{standaloneFilterColumns?.length > 0 && (
						<HeaderFilterModelForm
							standaloneFilterColumns={standaloneFilterColumns}
							filterModel={filterModel}
							onSubmit={onSubmitHeaderFilters}
							standaloneFilters={standaloneFilters}
							modelConfig={modelConfig}
							combos={combos}
						/>
					)}
				</Grid>
			</Grid>
		</Stack>
	);
};

export default memo(GridBaseToolbar);
